import type { SinceLastLoginData } from '../../hooks/dashboard/useSinceLastLogin';
import type { HomeListDestination } from './useOpenListFromHome';

export type SinceLastVisitCellKey =
  | 'findings'
  | 'signals'
  | 'assets'
  | 'executions';

export type SinceLastVisitTotals = Record<SinceLastVisitCellKey, number>;

export type SinceLastVisitCell = {
  key: SinceLastVisitCellKey;
  value: number;
  total: number;
  severity: SinceLastLoginData['findings'] | null;
  destination: HomeListDestination;
};

const NEW_FINDING_SEVERITIES = [
  { severity: 'Critical', key: 'critical' },
  { severity: 'High', key: 'high' },
  { severity: 'Medium', key: 'medium' },
  { severity: 'Low', key: 'low' },
] as const;

export type NewFindingSeverityCount = {
  severity: (typeof NEW_FINDING_SEVERITIES)[number]['severity'];
  count: number;
};

export const listNewFindingSeverities = (
  findings: SinceLastLoginData['findings'],
): NewFindingSeverityCount[] =>
  NEW_FINDING_SEVERITIES.map(({ severity, key }) => ({
    severity,
    count: findings[key],
  })).filter(({ count }) => count > 0);

export const buildSinceLastVisitCells = ({
  baselineAt,
  totals,
  deltas,
}: {
  baselineAt: Date | null;
  totals: SinceLastVisitTotals;
  deltas: SinceLastLoginData | undefined;
}): SinceLastVisitCell[] => {
  const hasBaseline = baselineAt !== null && deltas !== undefined;
  const createdSince = { createdSince: hasBaseline ? baselineAt : null };
  const valueFor = (key: SinceLastVisitCellKey) =>
    hasBaseline ? deltas[key].total : totals[key];

  return [
    {
      key: 'findings',
      value: valueFor('findings'),
      total: totals.findings,
      severity: hasBaseline ? deltas.findings : null,
      destination: {
        list: 'findings',
        filters: {
          ...createdSince,
          publishedFilter: hasBaseline ? 'published' : 'ALL',
        },
      },
    },
    {
      key: 'signals',
      value: valueFor('signals'),
      total: totals.signals,
      severity: null,
      destination: { list: 'signals', filters: createdSince },
    },
    {
      key: 'assets',
      value: valueFor('assets'),
      total: totals.assets,
      severity: null,
      destination: { list: 'assets', filters: createdSince },
    },
    {
      key: 'executions',
      value: valueFor('executions'),
      total: totals.executions,
      severity: null,
      destination: { list: 'executions', filters: createdSince },
    },
  ];
};
